/**
 * Client-side capture of an affiliate referral code.
 *
 * The ?ref=<code> lands on ANY page (usually a landing/sales page, not the
 * checkout), and the buyer may navigate around via client-side routing before
 * paying — so we persist the code on first sight and read it back at checkout.
 * Last-click wins: a fresh ?ref= overwrites an earlier one. Persistence has a TTL
 * so a months-old visit doesn't credit an unrelated purchase.
 */
const KEY = "sellfy_ref";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

interface StoredRef {
  code: string;
  at: number;
}

/**
 * Read ?ref= from the current URL and persist it (last-click). Call once at app
 * bootstrap so it runs regardless of which route the buyer entered on. Safe to
 * call in any environment — a missing/blocked localStorage is ignored.
 */
export function captureRefFromUrl(): void {
  try {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("ref")?.trim();
    if (!code) return;
    const value: StoredRef = { code: code.slice(0, 64), at: Date.now() };
    window.localStorage.setItem(KEY, JSON.stringify(value));
    reportClick(value.code, params.get("sub"));
  } catch {
    // no-op: private mode / disabled storage just means no attribution
  }
}

/**
 * Upgrade a legacy `?ref=` landing to the server-tracked path.
 *
 * A `/r/<CODE>` link is handled by the Worker, which writes the click and sets
 * an HttpOnly cookie. A plain `?ref=` never reaches the server — it is a static
 * page load the SPA handles client-side — so those links produced no click row
 * and no tamper-proof window. Those URLs are already printed, bookmarked and
 * pasted into WhatsApp, so they cannot be changed; this beacon gets them the
 * same treatment without touching the URL.
 *
 * Fire-and-forget on purpose: nothing on the page waits for it, and a failure
 * leaves the localStorage copy as the fallback it has always been.
 */
function reportClick(code: string, subId: string | null): void {
  try {
    const path = window.location.pathname.split("/").filter(Boolean);
    void fetch("/api/affiliate-track/click", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      // The cookie the server sets is the point of the call, so it has to be
      // allowed to come back.
      credentials: "include",
      keepalive: true,
      body: JSON.stringify({
        code,
        storeSlug: path[0] === "shop" ? path[1] : undefined,
        pageUrl: `${window.location.origin}${window.location.pathname}`,
        subId: subId?.slice(0, 120) || undefined,
      }),
    }).catch(() => {});
  } catch {
    // A blocked fetch just means this landing stays client-side only.
  }
}

/** The stored referral code if still within TTL, else undefined. */
export function getStoredRef(): string | undefined {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return undefined;
    const value = JSON.parse(raw) as StoredRef;
    if (!value?.code || Date.now() - value.at > TTL_MS) {
      window.localStorage.removeItem(KEY);
      return undefined;
    }
    return value.code;
  } catch {
    return undefined;
  }
}
